import { FarmRiskType } from '@ourbranch/lookups';

/**
 * Coverage C business rules shape — mirrors `packages/brand-config-be/src/types.ts`
 * (`FarmRiskTypeConfig.businessRules.coverageC`). Defined locally so farm-utils
 * stays decoupled from brand-config-be.
 */
export type FarmCoverageCRules = {
  min?: number;
  minPctOfCoverageA?: number;
  max: number | 'coverageA';
};

export type FarmCoverageCBusinessRules = {
  byResidenceUsage?: Record<string, FarmCoverageCRules>;
  byLossSettlement?: Record<
    string,
    {
      byResidenceUsage: Record<string, FarmCoverageCRules>;
    }
  >;
};

/**
 * Resolves the Coverage C rule entry for a given residence usage / loss settlement pair.
 *
 * FO2 defines rules via `byLossSettlement[…].byResidenceUsage[…]`; FO3 / FO1 use
 * `byResidenceUsage[…]` directly. We try the nested lookup first and fall through
 * to the flat one, matching the pattern already used in
 * `packages/underwriting-rules/.../farm-post-data-rules.ts` and
 * `staff-fe/.../farm-shared.validation-schema.js`.
 */
export function resolveFarmCoverageCRules({
  residenceUsage,
  lossSettlement,
  businessRulesC
}: {
  residenceUsage?: string | null;
  lossSettlement?: string | null;
  businessRulesC?: FarmCoverageCBusinessRules;
}): FarmCoverageCRules | undefined {
  if (!businessRulesC) {
    return undefined;
  }

  if (businessRulesC.byLossSettlement && lossSettlement && residenceUsage) {
    const rules = businessRulesC.byLossSettlement[lossSettlement]?.byResidenceUsage?.[residenceUsage];
    if (rules) {
      return rules;
    }
  }

  if (businessRulesC.byResidenceUsage && residenceUsage) {
    return businessRulesC.byResidenceUsage[residenceUsage];
  }

  return undefined;
}

/**
 * Computes the default Coverage C value for a dwelling risk.
 *
 * Context: GitHub issue #29084 — Forreston FO2/FO3 tenant-occupied risks were
 * defaulting to 50% / 75% of Coverage A. Those defaults then got clamped by
 * post-data-rules to the tenant max ($25,000), so risks appeared to "default to
 * the max." The correct default for a tenant rule (`{ min: 5000, max: 25000 }`)
 * is the configured minimum ($5,000).
 *
 * Resolution order:
 *   1. `minPctOfCoverageA` rule → `coverageA * minPctOfCoverageA` (e.g., FO3 Primary = 75% of CovA).
 *   2. Fixed-range rule (numeric `min` + numeric `max`) → `min`. This is the
 *      tenant case: start users at the floor, not the ceiling.
 *   3. No matching rule → legacy fallback (50% FO2, 75% FO3) to preserve
 *      behavior for carriers/risk-types without configured rules.
 */
export function getDefaultFarmCoverageC({
  riskType,
  residenceUsage,
  lossSettlement,
  coverageA,
  businessRulesC
}: {
  riskType: string;
  residenceUsage?: string | null;
  lossSettlement?: string | null;
  coverageA: number;
  businessRulesC?: FarmCoverageCBusinessRules;
}): number {
  const rules = resolveFarmCoverageCRules({ residenceUsage, lossSettlement, businessRulesC });

  if (rules?.minPctOfCoverageA != null) {
    return Math.round(coverageA * rules.minPctOfCoverageA);
  }

  if (rules && typeof rules.max === 'number' && rules.min != null) {
    return rules.min;
  }

  // Legacy fallback — carriers/risk-types with no Coverage C rule configured.
  if (riskType === FarmRiskType.Fo3) {
    return Math.round(coverageA * 0.75);
  }
  if (riskType === FarmRiskType.Fo2) {
    return Math.round(coverageA * 0.5);
  }

  return 0;
}
